import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";

const Gallery = () => {
  const categories = [
    "All",
    "Team",
    "Projects",
    "Customer Visits",
    "Documentation",
    "Celebrations",
  ];

  const categoryParagraphs = {
    Team: `At DVV, our leadership team and dedicated members form the backbone of every project we undertake. 
From strategizing new ventures to overseeing operations, their expertise ensures excellence. 
Collaboration, innovation, and commitment drive our day-to-day work. 
Every moment captured here reflects our passion for delivering quality and maintaining strong client relationships.`,
    Projects: `Our projects span residential, farmland, and commercial ventures that reflect our vision and quality standards. 
Each project is carefully planned and executed to ensure client satisfaction and long-term sustainability. 
The images below showcase our diverse range of completed and ongoing projects.`,
    "Customer Visits": `We value hands-on interaction with our clients. Our site visits allow customers to inspect progress, clarify doubts, and witness the quality of work firsthand. 
These images capture those meaningful engagements.`,
    Documentation: `Proper documentation ensures smooth transactions and transparent dealings. 
Here we capture moments of document verification, approvals, and record-keeping that demonstrate our commitment to transparency and professionalism.`,
    Celebrations: `At DVV, we celebrate milestones and achievements together. 
From team events to project completions, these moments highlight our company culture, camaraderie, and joyous work environment.`,
  };

  const [activeCategory, setActiveCategory] = useState("All");
  const [images, setImages] = useState([]);

  // Fetch gallery images from Firestore
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        const q = query(collection(db, "gallery"));
        const snapshot = await getDocs(q);
        const fetchedImages = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setImages(fetchedImages);
      } catch (error) {
        console.error("Failed to fetch gallery images:", error);
      }
    };

    fetchGalleryImages();
  }, []);

  const filteredImages =
    activeCategory === "All"
      ? images
      : images.filter((img) => img.category === activeCategory);

  const imageVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen pt-24 pb-14 px-4 bg-[#F7F1ED]">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-dark mb-6">
          DVV Gallery
        </h1>
        <p className="text-gray-600 mt-2">
          Leadership • Projects • Site Visits • Milestones
        </p>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap justify-center gap-3 mb-12">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition
              ${
                activeCategory === cat
                  ? "bg-brand-dark text-white shadow-md"
                  : "bg-white text-gray-700 hover:bg-gray-200"
              }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Hero Section */}
      {activeCategory !== "All" && filteredImages.length > 0 && (
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-6 mb-12 bg-white p-6 rounded-2xl shadow-md">
          <img
            src={filteredImages[0].imageUrl}
            alt={activeCategory}
            className="w-full md:w-1/2 rounded-xl object-cover h-72"
          />
          <div className="md:w-1/2">
            <h2 className="text-2xl font-bold mb-3">{activeCategory}</h2>
            <p className="text-gray-700 whitespace-pre-line">
              {categoryParagraphs[activeCategory]}
            </p>
          </div>
        </div>
      )}

      {/* Gallery Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {filteredImages.map((img, index) => (
          <motion.div
            key={img.id}
            className="relative group overflow-hidden rounded-2xl shadow-lg bg-white"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            variants={imageVariants}
          >
            <img
              src={img.imageUrl}
              alt={img.title}
              className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110"
            />

            {/* Overlay */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition duration-500 flex items-end">
              <div className="p-4">
                <p className="text-white font-semibold text-sm">{img.title}</p>
                {img.description && (
                  <span className="text-gray-200 text-xs">{img.description}</span>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Gallery;